import pmMilestoneService from '@/services/pmMilestoneService';

export default {
  namespaced: true,

  state: {
    milestones: [],
    total: 0,
    projectId: null,
    loading: false,
    error: null,
  },

  getters: {
    milestones: (state) => state.milestones,
    total: (state) => state.total,
    projectId: (state) => state.projectId,
    loading: (state) => state.loading,
    error: (state) => state.error,
    isEmpty: (state) => !state.loading && !state.error && state.milestones.length === 0,
    hasProjectContext: (state) => Boolean(state.projectId),
    milestoneOptions: (state) => state.milestones.map((item) => ({
      label: item.title,
      value: item.id,
    })),
    milestoneTitleById: (state) => (id) => {
      if (!id) return '';
      const milestone = state.milestones.find((item) => String(item.id) === String(id));
      return milestone?.title || '';
    },
  },

  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_MILESTONES(state, { items, total }) {
      state.milestones = items || [];
      state.total = typeof total === 'number' ? total : state.milestones.length;
    },
    ADD_MILESTONE(state, milestone) {
      state.milestones.unshift(milestone);
      state.total += 1;
    },
    UPDATE_MILESTONE(state, updated) {
      const index = state.milestones.findIndex((item) => item.id === updated.id);
      if (index !== -1) {
        state.milestones.splice(index, 1, updated);
      }
    },
    REMOVE_MILESTONE(state, id) {
      state.milestones = state.milestones.filter((item) => item.id !== id);
      state.total = Math.max(0, state.total - 1);
    },
    RESET(state) {
      state.milestones = [];
      state.total = 0;
      state.projectId = null;
      state.loading = false;
      state.error = null;
    },
  },

  actions: {
    setProjectContext({ commit, dispatch }, projectId) {
      commit('SET_PROJECT_ID', projectId);
      if (projectId) {
        return dispatch('fetchMilestones');
      }
      commit('SET_MILESTONES', { items: [], total: 0 });
      return Promise.resolve();
    },

    async fetchMilestones({ commit, state }) {
      if (!state.projectId) {
        commit('SET_MILESTONES', { items: [], total: 0 });
        return;
      }
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const data = await pmMilestoneService.listMilestones(state.projectId);
        commit('SET_MILESTONES', {
          items: data?.items || [],
          total: data?.total ?? 0,
        });
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load milestones';
        commit('SET_ERROR', message);
        commit('SET_MILESTONES', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchMilestoneDetail({ state }, id) {
      if (!state.projectId) {
        throw new Error('Project context is required');
      }
      return pmMilestoneService.getMilestone(state.projectId, id);
    },

    async createMilestone({ commit, state }, payload) {
      commit('SET_ERROR', null);
      try {
        const milestone = await pmMilestoneService.createMilestone(state.projectId, payload);
        commit('ADD_MILESTONE', milestone);
        return milestone;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to create milestone';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateMilestone({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const milestone = await pmMilestoneService.updateMilestone(state.projectId, id, payload);
        commit('UPDATE_MILESTONE', milestone);
        return milestone;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update milestone';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateMilestoneStatus({ commit, state }, { id, status, actualDate }) {
      commit('SET_ERROR', null);
      try {
        const payload = { status };
        if (actualDate) payload.actualDate = actualDate;
        const milestone = await pmMilestoneService.updateMilestoneStatus(state.projectId, id, payload);
        commit('UPDATE_MILESTONE', milestone);
        return milestone;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update milestone status';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async deleteMilestone({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        await pmMilestoneService.deleteMilestone(state.projectId, id);
        commit('REMOVE_MILESTONE', id);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to delete milestone';
        commit('SET_ERROR', message);
        throw error;
      }
    },
  },
};
